import * as prismic from "@prismicio/client";
import type { ClientConfig, ResponseLike } from "@prismicio/client";
import * as prismicNext from "@prismicio/next";
import { enableAutoPreviews } from "@prismicio/next";
import { LRUCache } from "lru-cache";
import config from "./slicemachine.config.json";

const cache = new LRUCache({
  max: 1000, // 1000 entries
  ttl: 1000 * 60 * 60, // 1 hour
});

/**
 * The project's Prismic repository name.
 */
export const repositoryName =
  process.env.NEXT_PUBLIC_PRISMIC_ENVIRONMENT || config.repositoryName;
const endpoint = prismic.getRepositoryEndpoint(repositoryName);

/**
 * A list of Route Resolver objects that define how a document's `url` field is resolved.
 *
 * {@link https://prismic.io/docs/route-resolver#route-resolver}
 */
const routes: prismic.ClientConfig["routes"] = [
  {
    type: "games_page",
    path: "/games",
  },
  {
    type: "game_details_page",
    path: "/games/:uid",
  },
  {
    type: "premium_game_details_page",
    path: "/games/:uid",
  },
  {
    type: "multi_game_details_page",
    path: "/games/:uid",
  },
];

/**
 * Creates a Prismic client for the project's repository. The client is used to
 * query content from the Prismic API.
 *
 * @param config - Configuration for the Prismic client.
 */
export const createClient = (config: prismicNext.CreateClientConfig = {}) => {
  const client = prismic.createClient(endpoint, {
    routes,
    fetchOptions:
      process.env.NODE_ENV === "production"
        ? { next: { tags: ["prismic"] }, cache: "force-cache" }
        : { next: { revalidate: 5 } },
    ...config,
    /**
     * Implement caching for prismic client
     * Must clone response to avoid error `TypeError: body used already`
     * this is because client will invoke `response.json()` after each request
     */
    fetch: async (url, options) => {
      const key = JSON.stringify({ url, options });

      if (cache.has(key)) {
        const response = cache.get(key) as Response;
        return response.clone() as unknown as ResponseLike;
      }

      const response: Response = await fetch(url, options);

      if (response.ok) {
        cache.set(key, response.clone());
      }

      return response as unknown as ResponseLike;
    },
  });

  const { req, previewData } = config;

  prismicNext.enableAutoPreviews({
    req,
    client,
    previewData,
  });

  return client;
};

/**
 * Creates a Prismic client for the project's repository. The client is used to
 * query content from the Prismic API.
 *
 * @param config - Configuration for the Prismic client.
 */
export const createClientAppRouter = (config: ClientConfig = {}) => {
  const client = prismic.createClient(endpoint, {
    routes,
    fetchOptions:
      process.env.NODE_ENV === "production"
        ? { next: { tags: ["prismic"] }, cache: "force-cache" }
        : { next: { revalidate: 5 } },
    ...config,
    /**
     * Implement caching for prismic client
     * Must clone response to avoid error `TypeError: body used already`
     * this is because client will invoke `response.json()` after each request
     */
    fetch: async (url, options) => {
      const key = JSON.stringify({ url, options });

      if (cache.has(key)) {
        const response = cache.get(key) as Response;
        return response.clone() as unknown as ResponseLike;
      }

      const response: Response = await fetch(url, options);

      if (response.ok) {
        cache.set(key, response.clone());
      }

      return response as unknown as ResponseLike;
    },
  });

  enableAutoPreviews({ client });

  return client;
};
